<template>
  <div :class="[style.block, style.offPaddingTop]">
    <InputStyle
      description="Заголовок"
      placeholder="Введите заголовок"
      size="M"
      textarea
      :modelValue="questionTitle"
      @update:modelValue="setQuestionTitle"
      @input="setQuestionTitle"
    />
  </div>

  <div :class="style.block">
    <InputStyle
      description="Описание"
      placeholder="Введите описание"
      size="M"
      textarea
      :modelValue="questionDescription"
      @update:modelValue="setQuestionDescription"
      @input="setQuestionDescription"
    />
  </div>

  <div :class="style.block">
    <input
      ref="fileInput"
      type="file"
      accept="image/*, video/*"
      hidden
      @change="setInformationImage"
    />

    <InputFieldMedia
      hasImage
      text="Файл"
      description="Медиа"
      isFile
      :image="informationImage"
      :mediaType="informationMediaType"
      :actions="fileActions"
      @upload="openFileDialog"
    />
  </div>

  <div :class="style.block">
    <InputStyle
      description="Текст под кнопкой"
      placeholder="Введите текст"
      size="M"
      :modelValue="textBellowButton"
      @update:modelValue="setTextBellowButton"
    />
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType, markRaw} from "vue";

import style from "../../QuestionPanelComponent.module.scss";
import {DOMAIN} from "@/http";

import {questions} from "@/page/quiz/typeQuiz";
import InputFieldMedia from "@/elements/UI/input/Media/InputFieldMedia.vue";

import deleteIcon from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";
import {modifyQuiz} from "@/page/quiz/scriptQuiz";

export default defineComponent({
  name: "InformationQuestionContent",

  components: {
    InputStyle,
    InputFieldMedia
  },

  props: {
    question: {
      type: Object as PropType<questions | null>,
      default: null
    },

    questionIndex: {
      type: Number,
      default: -1
    }
  },

  data() {
    return {
      style,
      localImg: "",
      cachedFilePreviewList: {} as Record<string, string>
    };
  },

  beforeUnmount() {
    this.revokeLocalImage();
    this.revokeAllCachedPreviews();

    const image = this.question?.information || "";

    if (image.startsWith("blob:")) {
      URL.revokeObjectURL(image);
    }
  },

  methods: {
    setQuestionTitle(value: unknown): void {
      if (!this.question) {
        return;
      }

      this.question.question = this.getInputValue(value);
    },

    setQuestionDescription(value: unknown): void {
      if (!this.question) {
        return;
      }

      if (!this.question.contacts) {
        this.question.contacts = {};
      }

      this.question.contacts.paragraph = this.getInputValue(value);
    },

    getInputValue(value: unknown): string {
      if (typeof value === "string") {
        return value;
      }

      if (value && typeof value === "object" && "target" in value) {
        const target = (value as Event).target as
          HTMLInputElement | HTMLTextAreaElement | null;

        return target?.value ?? "";
      }

      return "";
    },

    openFileDialog(): void {
      const input = this.$refs.fileInput as HTMLInputElement | undefined;

      if (!input) {
        return;
      }

      input.value = "";
      input.click();
    },

    setInformationImage(event: Event): void {
      if (!this.question) {
        return;
      }

      const input = event.target as HTMLInputElement | null;
      const file = input?.files?.[0];

      if (!file) {
        return;
      }

      const oldImage = this.informationPath;
      const fileName = this.createInformationFileName(file);
      const newFile = new File([file], fileName, {
        type: file.type
      });
      const preview = URL.createObjectURL(newFile);

      this.revokeLocalImage();
      this.revokeCachedPreview(oldImage);
      this.removeCachedFile(oldImage);
      this.removeCachedFile(fileName);

      this.cachedFiles.push(newFile);

      this.localImg = preview;
      this.setInformationValue(fileName);
      this.clearFileInput();
    },

    deleteInformationImage(): void {
      if (!this.question) {
        return;
      }

      const currentImage = this.informationPath;

      this.revokeLocalImage();
      this.revokeCachedPreview(currentImage);
      this.removeCachedFile(currentImage);

      if (currentImage.startsWith("blob:")) {
        URL.revokeObjectURL(currentImage);
      }

      this.localImg = "";
      this.setInformationValue("");

      this.clearFileInput();
    },

    createInformationFileName(file: File): string {
      const ext = file.name.split(".").pop() || "";
      const questionType = this.question?.type || "information";
      const uniqueSuffix = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      return `${this.resolvedQuestionIndex}-${questionType}-${uniqueSuffix}.${ext}`;
    },

    setInformationValue(value: string): void {
      if (!this.question) {
        return;
      }

      this.question.information = value;
    },

    removeCachedFile(fileName: string): void {
      if (!fileName) {
        return;
      }

      const index = this.cachedFiles.findIndex(
        (file) => file.name === fileName
      );

      if (index !== -1) {
        this.cachedFiles.splice(index, 1);
      }
    },

    getCachedFilePreview(fileName: string): string {
      if (!fileName) {
        return "";
      }

      const savedPreview = this.cachedFilePreviewList[fileName];

      if (savedPreview) {
        return savedPreview;
      }

      const cachedFile = this.cachedFiles.find(
        (file) => file.name === fileName
      );

      if (!cachedFile) {
        return "";
      }

      const previewUrl = URL.createObjectURL(cachedFile);

      this.cachedFilePreviewList = {
        ...this.cachedFilePreviewList,
        [fileName]: previewUrl
      };

      return previewUrl;
    },

    revokeLocalImage(): void {
      if (this.localImg?.startsWith("blob:")) {
        URL.revokeObjectURL(this.localImg);
      }
    },

    revokeCachedPreview(fileName: string): void {
      if (!fileName) {
        return;
      }

      const previewUrl = this.cachedFilePreviewList[fileName];

      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      const updatedPreviewList = {...this.cachedFilePreviewList};

      delete updatedPreviewList[fileName];

      this.cachedFilePreviewList = updatedPreviewList;
    },

    revokeAllCachedPreviews(): void {
      Object.values(this.cachedFilePreviewList).forEach((previewUrl) => {
        if (previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }
      });
    },

    clearFileInput(): void {
      const input = this.$refs.fileInput as HTMLInputElement | undefined;

      if (input) {
        input.value = "";
      }
    },

    isVideoPath(path = ""): boolean {
      return /\.(mp4|webm|ogg)(?:$|[?#])/i.test(path);
    },

    setTextBellowButton(value: string): void {
      if (!this.question) {
        return;
      }

      modifyQuiz("set", value, ["textBellowButton"]);
    }
  },

  computed: {
    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles || [];
    },

    resolvedQuestionIndex(): number {
      if (this.questionIndex >= 0) {
        return this.questionIndex;
      }

      const selectedQuestion = this.$quizStore.getSelectedQuestion;

      if (typeof selectedQuestion === "number" && selectedQuestion >= 0) {
        return selectedQuestion;
      }

      const questions = this.$quizStore.getQuiz?.questions;

      if (Array.isArray(questions) && this.question) {
        const questionIndex = questions.indexOf(this.question);

        if (questionIndex !== -1) {
          return questionIndex;
        }
      }

      return 0;
    },

    questionTitle(): string {
      return this.question?.question || "";
    },

    questionDescription(): string {
      return this.question?.contacts?.paragraph || "";
    },

    informationPath(): string {
      return this.question?.information || "";
    },

    hasInformationImage(): boolean {
      return Boolean(this.localImg || this.informationPath);
    },

    informationImage(): string {
      if (this.localImg) {
        return `url('${this.localImg}')`;
      }

      const path = this.informationPath;

      if (!path) {
        return "";
      }

      if (path.startsWith("blob:") || path.startsWith("data:")) {
        return `url('${path}')`;
      }

      if (path.startsWith("http://") || path.startsWith("https://")) {
        return `url('${path}')`;
      }

      const cachedPreview = this.getCachedFilePreview(path);

      if (cachedPreview) {
        return `url('${cachedPreview}')`;
      }

      return `url('https://${DOMAIN}/api/img?name=${encodeURIComponent(path)}')`;
    },

    informationMediaType(): "image" | "video" {
      return this.isVideoPath(this.informationPath) ? "video" : "image";
    },

    fileActions(): ListActionItem[] {
      return [
        {
          text: "Удалить",
          icon: deleteIcon,
          variant: "button",
          buttonComponent: markRaw(GhostButtonError),
          handler: this.deleteInformationImage,
          disabled: !this.hasInformationImage
        }
      ];
    },

    textBellowButton(): string {
      return modifyQuiz("get", "", ["textBellowButton"]) ?? "";
    }
  },

  watch: {
    question(): void {
      this.revokeLocalImage();
      this.revokeAllCachedPreviews();

      this.localImg = "";
      this.cachedFilePreviewList = {};

      this.clearFileInput();
    },

    informationPath(newValue: string, oldValue: string): void {
      if (oldValue && oldValue !== newValue) {
        this.revokeCachedPreview(oldValue);
      }

      if (!newValue) {
        this.revokeLocalImage();
        this.localImg = "";
        this.clearFileInput();
      }

      if (newValue && newValue !== this.localImg) {
        this.localImg = "";
      }
    },

    questionIndex(): void {
      this.revokeLocalImage();
      this.revokeAllCachedPreviews();

      this.localImg = "";
      this.cachedFilePreviewList = {};

      this.clearFileInput();
    }
  }
});
</script>
